import { PriceChange } from "@crypto-trading/common/browser";
import { ExchangeCode } from "@/lib/market-data/market-data.types";

export type CandleInterval = "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";

export const CANDLE_INTERVAL_MS: Record<CandleInterval, number> = {
  "1m": 60 * 1000,
  "5m": 5 * 60 * 1000,
  "15m": 15 * 60 * 1000,
  "30m": 30 * 60 * 1000,
  "1h": 60 * 60 * 1000,
  "4h": 4 * 60 * 60 * 1000,
  "1d": 24 * 60 * 60 * 1000,
};

/** Max candles per paginated REST request (used for window sizing and pagination heuristics). */
const EXCHANGE_CANDLE_PAGE_SIZES: Partial<Record<ExchangeCode, number>> = {
  binance: 500,
  bybit: 500,
  gate: 500,
  kucoin: 200,
  hyperliquid: 5000,
  asterdex: 500,
};

export function getExchangeCandlePageSize(
  exchange: ExchangeCode,
  pageLimit = 500
): number {
  const exchangeCap = EXCHANGE_CANDLE_PAGE_SIZES[exchange] ?? pageLimit;
  return Math.min(pageLimit, exchangeCap);
}

export interface CandlePoint {
  startTime: number;
  openPrice: string;
  highPrice: string;
  lowPrice: string;
  closePrice: string;
  volume?: string;
}

export interface CandleFetchParams {
  symbol: string;
  interval: CandleInterval;
  startTime?: number;
  endTime?: number;
  limit?: number;
  /** Required when fetching via our API (CORS-blocked exchanges). */
  userId?: string;
}

export interface CandleUpdate {
  symbol: string;
  exchange: ExchangeCode;
  interval: CandleInterval;
  candle: CandlePoint;
  timestamp: number;
}

export type CandleUpdateCallback = (update: CandleUpdate) => void;
export type CandleErrorCallback = (err: Error) => void;

export interface ExchangeCandleRestAdapter {
  fetchCandles(params: CandleFetchParams): Promise<CandlePoint[]>;
}

export interface ExchangeCandleWsAdapter {
  subscribe(
    symbol: string,
    interval: CandleInterval,
    onUpdate: CandleUpdateCallback,
    onError?: CandleErrorCallback
  ): () => void;
}

export interface CandleFetchErrorMeta {
  code:
    | "TOKEN_NOT_AVAILABLE"
    | "HISTORY_OUT_OF_RANGE"
    | "NETWORK"
    | "RATE_LIMIT"
    | "UNSUPPORTED"
    | "UNKNOWN";
  message: string;
}

const RAW_EXCHANGE_REQUEST_ERROR = /request failed/i;

/** Exchange HTTP failures stay out of the chart. Callers render this string instead. */
export function toPublicChartErrorMessage(message: string): string {
  if (RAW_EXCHANGE_REQUEST_ERROR.test(message)) {
    return "Price chart is temporarily unavailable";
  }
  return message;
}

export function toPriceChange(candle: CandlePoint): PriceChange {
  return {
    startTime: candle.startTime,
    openPrice: candle.openPrice,
    highPrice: candle.highPrice,
    lowPrice: candle.lowPrice,
    closePrice: candle.closePrice,
    volume: candle.volume ?? "0",
    turnover: "0",
  };
}

